<template>
    <div class="broadcast-list-page">
        <vue-headful title="ALICE | Broadcast"></vue-headful>
        <div class="sub-header">
            <p>All Broadcasts <span v-if="isLineBroadcastJobDataLoaded">({{ lineBroadcastJobData.totalElements }})</span></p>
            <div>
                 <div @click="onCreateNewAction" class="new-broadcast-btn">
                       <i class='bx bx-plus'></i>
                        New Broadcast
                 </div>
            </div>
        </div>
        <div class="broadcast-list-container">
            <div class="broadcast-filter">
                <div class="broadcast-date-filter">
                    <date-range-picker
                    v-model="dateRange"
                    :show-dropdowns="true"
                    :ranges="dateRangesSelector"
                    opens="right"
                    @update="onChangeCriteria"
                    class="date-picker"
                    >
                        <div class="date-selected" slot="input">
                            <i class="bx bx-calendar"></i>
                            <p v-if="dateRange.startDate && dateRange.endDate">
                                <span>{{ formatJSDate(dateRange.startDate) }} - {{ formatJSDate(dateRange.endDate) }}</span>
                            </p>
                            <p v-else>All Dates</p>
                        </div>
                    </date-range-picker>
                </div>
                <div class="broadcast-status-filter" ref="broadcastStatusFilterRef">
                    <div class="status-selected" @click="onClickStatusDropdownBox">
                        <p class="selected-option-text">{{ selectedStatusFilter.label }}</p>
                        <i class="bx bx-caret-up" v-if="isStatusDropdownBoxClicked"></i>
                        <i class="bx bx-caret-down" v-else></i>
                    </div>
                    <div class="status-dropdown" v-if="isStatusDropdownBoxClicked">
                        <div class="status-item" v-for="option in statusDropdownOption" :key="option.value" :class="{'selected-status': option.value === selectedStatusFilter.value}" @click="onClickStatusDropdownOption(option.value)">
                            <p>{{ option.label }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="broadcast-list-grid">
                <div class="grid-header">
                    <div class="grid-header-item">BROADCAST NAME</div>
                    <div class="grid-header-item">TARGET</div>
                    <div class="grid-header-item grid-header-recipients">RECIPIENTS</div>
                    <div class="grid-header-item">STATUS</div>
                    <div class="grid-header-item">BROADCAST TIME</div>
                    <div class="grid-header-item"></div>
                </div>
                <div class="grid-content">
                    <div v-for="broadcast in lineBroadcastJobData.contents" :key="broadcast.id" class="broadcast-item grid-row">
                        <div class="grid-cell">
                          <p>
                            {{ broadcast.name }}
                            <span v-if="broadcast.is_archived" class="archive-badge">
                              Archived
                            </span>
                          </p>
                        </div>
                        <div class="grid-cell">
                              <template v-if="broadcast.target === 'AUTHENTICATED'">
                                <p>
                                  {{ displayTargetAudienceType(broadcast.target, Array.isArray(broadcast.listOfAuthenticatedGroups) ? broadcast.listOfAuthenticatedGroups.length : 0) }}
                                </p>
                                <div class="register-group-tag">
                                  <div v-for="group in formatAuthenticatedGroup(broadcast.listOfAuthenticatedGroups)" :key="group.id">
                                    <TargetGroupTagWithTooltip
                                      :group-detail="group"
                                    />
                                  </div>
                                </div>
                              </template>
                              <template v-else-if="broadcast.target === 'AREA'">
                                <p>
                                  {{ displayTargetAudienceType(broadcast.target, Array.isArray(broadcast.listOfAreaCodes) ? broadcast.listOfAreaCodes.length : 0) }}
                                </p>
                                <div class="register-group-tag">
                              <div v-for="area in formatAreaCodes(broadcast.listOfAreaCodes)" :key="area.id">
                                <LineBroadcastTag
                                        :tagText="area"
                                        class="group-name-tag"
                                    />
                                  </div>
                                </div>
                              </template>
                              <template v-else-if="broadcast.target === 'CUSTOM'">
                                <p>{{ displayTargetAudienceType(broadcast.target) }}</p>
                                <LineBroadcastTag :tagText="broadcast.customTargetGroupName || 'Unknown'" class="group-name-tag" />
                              </template>
                              <template v-else>
                                <p>{{ displayTargetAudienceType(broadcast.target) }}</p>
                              </template>
                        </div>
                        <div class="grid-cell recipients-custom-group">
                            <p v-if="broadcast.totalRecipient < 0" style="text-align: end;">N/A</p>
                            <p v-else style="text-align: end;">{{ broadcast.totalRecipient | number }}</p>
                            <div class="download-recipients-icon" @click="onClickDownloadRecipientsCustomGroup(broadcast.id)" v-if="broadcast.target === 'CUSTOM'">
                              <i class="bx bx-download"></i>
                            </div>
                            <div style="width: 14px;" v-else>
                            </div>
                        </div>
                        <div class="grid-cell status-badge-container">
                            <div class="status-badge" :class="`badge--${broadcast.status.toLowerCase()}`">
                                <p>{{ displayBroadcastStatus(broadcast.status) }}</p>
                            </div>
                        </div>
                        <div class="grid-cell display-time-container">
                            <p>{{ formatISODate(broadcast.displayTime) }}</p>
                        </div>
                        <div class="grid-cell">
                            <div class="action-tooltip-container" :ref="visibleTooltipBroadcastID === broadcast.id ? 'actionTooltipContainerRef' : null">
                                <div v-if="broadcast.status !== 'IN_PROGRESS' && !broadcast.is_archived" @click="onClickBroadcastAction(broadcast)">
                                    <i class="bx bx-dots-horizontal-rounded"></i>
                                </div>
                                <div v-if="visibleTooltipBroadcastID === broadcast.id" class="action-tooltip">
                                  <div v-if="canCopyBroadcast(broadcast.status)" class="tooltip-item" @click="onCopyAction">
                                    <i class="bx bx-copy"></i>
                                    <p>Copy</p>
                                  </div>
                                  <div v-if="broadcast.status === 'SENT' || broadcast.status === 'FAILED'" class="tooltip-item" @click="onArchivedAction">
                                    <i class="bx bx-archive"></i>
                                    <p>Archived</p>
                                  </div>
                                  <div v-if="broadcast.status === 'SCHEDULED'" class="tooltip-item" @click="onDeleteBroadcastAction">
                                    <i class="bx bx-trash"></i>
                                    <p>Delete</p>
                                  </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="infinite-loading-section">
                        <infinite-loading
                          @infinite="infiniteHandler"
                          force-use-infinite-wrapper=".grid-content"
                          :distance=20
                          ref="infiniteLoadingRef"
                          >
                            <span slot="spinner">
                                <i class='bx bx-loader-alt bx-spin'></i>
                            </span>
                            <span slot="no-more"></span>
                            <span slot="no-results"></span>
                        </infinite-loading>
                    </div>
                    <!-- No Data States -->
                     <template v-if="isLineBroadcastJobDataLoaded">
                       <div v-if="lineBroadcastJobData.totalElements <= 0" class="no-data-section">
                         <div class="no-data-broadcast">
                           <img src="@/assets/images/no_broadcast_list.svg" alt="No Data" class="no-data-image" />
                           <p class="no-data-text">No data available</p>
                           <p class="text-center">There are currently no broadcast.<br/>Please create <router-link to="/line-broadcast/create" class="link-text">new broadcast</router-link></p>
                          </div>
                        </div>
                        <div v-else-if="lineBroadcastJobData.contents.length <= 0" class="no-result-section">
                          <NoResultsFound class="no-result-broadcast" />
                        </div>
                    </template>
                </div>
            </div>
            <!--Delete Confirmation Modal -->
            <div class="confirmation-action-modal" v-if="isModalConfirmDeleteOpen">
              <div class="card-content">
                <div class="main-icon">
                  <img src="@/assets/images/warning-icon.png" alt="warning-icon" />
                </div>
                <div class="modal-title">
                  <p>Delete Broadcast</p>
                </div>
                  <p class="modal-description truncated-text">Are you sure you want to delete this broadcast? <br/>
                    This action cannot be undone.</p>
                <div class="modal-confirm-send-now-footer">
                  <button class="modal-btn cancel-btn" @click="onCancelDeleteLineBroadcast">Cancel</button>
                  <button class="modal-btn confirm-btn" @click="onConfirmDeleteLineBroadcast">Delete</button>
                </div>
              </div>
            </div>
            <!--Archive Confirmation Modal -->
            <div class="confirmation-action-modal" v-if="isModalConfirmArchiveOpen">
              <div class="card-content">
                <div class="main-icon warning-archive-icon">
                  <img src="@/assets/images/warning-icon-archive.png" alt="warning-icon" />
                </div>
                <div class="modal-title">
                  <p>Archive {{ selectedBroadcastData.name }}</p>
                </div>
                  <p class="modal-description truncated-text">Are you sure you want to archive {{ selectedBroadcastData.name }}? <br/>
                    This action cannot be undone.</p>
                <div class="modal-archive-footer">
                  <button class="modal-btn cancel-btn" @click="onCancelArchiveLineBroadcast">Cancel</button>
                  <button class="modal-btn confirm-btn" @click="onConfirmArchiveLineBroadcast">Archive</button>
                </div>
              </div>
            </div>
            <!--Auto Archive Modal -->
            <div class="confirmation-action-modal" v-if="isModalAutoArchiveOpen">
              <div class="card-content">
                <div
                  @click="onCancelAutoArchiveLineBroadcast"
                  class="close-icon"
                >
                  <i class="bx bx-x"></i>
                </div>
                <div class="main-icon warning-archive-icon">
                  <img src="@/assets/images/info-circle-icon.png" alt="info-circle-icon" />
                </div>
                <div class="modal-title">
                  <p>Your media storage space is full</p>
                </div>
                  <p class="modal-description truncated-text">Some older broadcasts will be archived to create a new broadcast. Are you sure you want to archived the older broadcasts? This action cannot be undone.</p>
                <div class="modal-auto-archive-footer">
                  <button class="modal-btn cancel-btn" @click="onCancelAutoArchiveLineBroadcast">Cancel</button>
                  <button class="modal-btn confirm-btn" @click="onConfirmAutoArchiveLineBroadcast">Create a new broadcast</button>
                </div>
              </div>
            </div>
            <!--Media storage full Modal -->
            <div class="confirmation-action-modal" v-if="isModalMediaStorageFullOpen">
              <div class="card-content">
                <div
                  @click="onCloseModalMediaStorageFull"
                  class="close-icon"
                >
                  <i class="bx bx-x"></i>
                </div>
                <div class="main-icon warning-archive-icon">
                  <img src="@/assets/images/info-circle-icon.png" alt="info-circle-icon" />
                </div>
                <div class="modal-title">
                  <p>Your media storage space is full</p>
                </div>
                  <p class="modal-description truncated-text">You have to delete some scheduled broadcasts to increase<br/>the space and then you can create a new broadcast</p>
                <div class="modal-auto-archive-footer">
                  <button class="modal-btn cancel-btn" @click="onCloseModalMediaStorageFull">OK</button>
                </div>
              </div>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTime } from 'luxon';
import { mapGetters, mapActions } from 'vuex';
import InfiniteLoading from 'vue-infinite-loading';
import uniqid from 'uniqid';
import SMMregisterGroup from '@/utiles/constants/SMMregisterGroup.json';
import LineBroadcastTag from '@/components/Tag/Broadcast/Line/LineBroadcastTag.vue';
import { debounce } from 'underscore';
import { downloadFileHelper, getFilenameFromContentDispositionHelper } from '@/utiles/exportFileHelper';
import aliceApi from '@/alice-api';
import NoResultsFound from '../../NoResultsFound.vue';
import TargetGroupTagWithTooltip from '../../Tooltip/Broadcast/Line/TargetGroupTagWithTooltip.vue';
import SMMstaticData from '../../../utiles/constants/SMMstaticData';

export default {
  name: 'LineBroadcastList',
  components: {
    InfiniteLoading,
    TargetGroupTagWithTooltip,
    LineBroadcastTag,
    NoResultsFound
  },
  data() {
    return {
      statusDropdownOption: {
        ALL: { value: 'ALL', label: 'All Status' },
        SENT: { value: 'SENT', label: 'Sent' },
        FAILED: { value: 'FAILED', label: 'Failed' },
        SCHEDULED: { value: 'SCHEDULED', label: 'Scheduled' },
        IN_PROGRESS: { value: 'IN_PROGRESS', label: 'In Progress' },
      },
      isStatusDropdownBoxClicked: false,
      selectedStatusFilter: {
        value: 'ALL',
        label: 'All Status',
      },
      dateRange: {
        startDate: null,
        endDate: null,
      },
      currentDate: DateTime.now().toUTC(),
      isBroadcastActionClicked: false,
      cursor: {},
      limit: 10,
      dateRangesSelector: {},
      visibleTooltipBroadcastID: null,
      isModalConfirmDeleteOpen: false,
      targetAudience: {
        ALL: { value: 'ALL', label: 'All Friends' },
        CUSTOM: { value: 'CUSTOM', label: 'Custom group' },
        AUTHENTICATED: { value: 'AUTHENTICATED', label: 'Register group' },
        UNAUTHENTICATED: { value: 'UNAUTHENTICATED', label: 'Non-register group' },
        AREA: { value: 'AREA', label: 'Area' },
      },
      isLineBroadcastJobDataLoaded: false,
      isModalConfirmArchiveOpen: false,
      isModalAutoArchiveOpen: false,
      isModalMediaStorageFullOpen: false,
      autoArchiveBroadcastId: null,
      selectedBroadcastData: null,
      //  TODO: this data is mocked for now, should be replaced with real data from API
      registerGroupData: [
        {
          id: 1,
          name: 'ผู้ขาย',
          subGroups: [
            {
              code: '0101',
              name: 'แผงค้า'
            },
            {
              code: '0103',
              name: 'ผู้เช่าอาคารพาณิชย์'
            },
            {
              code: '0104',
              name: 'แผงอาหาร'
            },
            {
              code: '0105',
              name: 'ซุ้มน้ำอัดลม'
            },
            {
              code: '0106',
              name: 'ซุ้มพลาสติก'
            }
          ]
        },
        {
          id: 2,
          name: 'ผู้ขายท้ายรถ',
          subGroups: [
            {
              code: '0201',
              name: 'ผู้ขายท้ายรถ (อาคารรถผักมีจุดจอดประจำ)'
            },
            {
              code: '0202',
              name: 'ผู้ขายท้ายรถ (อาคารรถผักไม่มีจุดจอดประจำ)'
            },
            {
              code: '0203',
              name: 'ผู้ขายท้ายรถ (ผลไม้ตามฤดูกาล)'
            }
          ]
        },
        {
          id: 3,
          name: 'ผู้เข็นเร่',
          subGroups: [
            {
              code: '0301',
              name: 'ผู้เข็นเร่'
            }
          ]
        },
        {
          id: 4,
          name: 'ผู้เช่าห้องเย็น',
          subGroups: [
            {
              code: '0401',
              name: 'เช่าเหมาห้อง'
            }
          ]
        },
        {
          id: 5,
          name: 'ผู้เช่า',
          subGroups: [
            {
              code: '0501',
              name: 'ผู้เช่าอาคารที่พักอาศัย'
            },
            {
              code: '0502',
              name: 'ผู้เช่าที่ดิน/สำนักงาน'
            }
          ]
        },
        {
          id: 6,
          name: 'ผู้ขนส่ง',
          name_en: 'Coordinators/Organizers',
          subGroups: [
            {
              code: '0601',
              name: 'ผู้ส่งสินค้า'
            },
            {
              code: '0602',
              name: 'ผู้ขนส่งสินค้าภายใน (ซาเล้ง มีทะเบียนติด)'
            }
          ]
        }
      ],
      popperInstance: null,
      authenticatedGroupConstants: SMMregisterGroup,
    };
  },
  async mounted() {
    this.clearLineBroadcastJobData();
    this.selectedStatusFilter = this.statusDropdownOption.ALL;
    this.dateRange.startDate = null;
    this.dateRange.endDate = null;
    this.dateRangesSelectorSetup();
    document.addEventListener('mousedown', this.handleClickOutsideStatusDropdown, true);
    document.addEventListener('mousedown', this.handleClickOutsideActionTooltip, true);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleClickOutsideStatusDropdown, true);
    document.removeEventListener('mousedown', this.handleClickOutsideActionTooltip, true);
  },
  methods: {
    ...mapActions({
      loadLineBroadcastJobData: 'lineBroadcast/loadLineBroadcastJobData',
      clearLineBroadcastJobData: 'lineBroadcast/clearLineBroadcastJobData',
      deleteLineBroadcastJob: 'lineBroadcast/deleteLineBroadcastJob',
      archiveLineBroadcastJob: 'lineBroadcast/archiveLineBroadcastJob',
      checkAutomaticArchive: 'lineBroadcast/checkAutomaticArchive',
    }),
    async getLineBroadcastJobList() {
      this.cursor = this.lineBroadcastJobData.cursor;
      const param = {
        limit: this.limit,
        cursor: this.cursor,
        filter: {
          status: this.selectedStatusFilter.value === 'ALL' ? null : this.selectedStatusFilter.value,
          time: null,
        },
      };
      if (this.dateRange.startDate && this.dateRange.endDate) {
        param.filter.time = {
          sinceDate: this.formatToISOStartOfDay(this.dateRange.startDate),
          untilDate: this.formatToISOEndOfDay(this.dateRange.endDate),
        };
      }
      await this.loadLineBroadcastJobData(param);
    },
    onClickStatusDropdownBox() {
      this.isStatusDropdownBoxClicked = !this.isStatusDropdownBoxClicked;
    },
    onClickStatusDropdownOption(value) {
      const newSelectedStatusOption = this.statusDropdownOption[value];
      if (this.selectedStatusFilter.value !== newSelectedStatusOption.value) {
        // we will trigger onChangeCriteria only if we got difference status.value
        this.selectedStatusFilter = newSelectedStatusOption;
        this.onChangeCriteria();
      }
      this.isStatusDropdownBoxClicked = false;
    },
    handleClickOutsideStatusDropdown(event) {
      const { broadcastStatusFilterRef } = this.$refs;
      if (!broadcastStatusFilterRef || broadcastStatusFilterRef.contains(event.target)) return;
      this.isStatusDropdownBoxClicked = false;
    },
    handleClickOutsideActionTooltip(event) {
      if (this.visibleTooltipBroadcastID === null || this.isModalConfirmDeleteOpen || this.isModalConfirmArchiveOpen || this.isModalAutoArchiveOpen) return;
      // Always select the first actionTooltipContainerRef becase we only show one tooltip at a time
      // TODO Recheck condition again
      const ref = this.$refs.actionTooltipContainerRef ? this.$refs.actionTooltipContainerRef[0] : null;
      if (!ref || ref.contains(event.target)) return;
      this.visibleTooltipBroadcastID = null;
    },
    formatISODate(date) {
      if (!date) return '';
      return DateTime.fromISO(date).setZone('Asia/Bangkok').toFormat('dd/MM/yyyy HH:mm');
    },
    formatJSDate(date) {
      if (!date) return '';
      return DateTime.fromJSDate(date).setZone('Asia/Bangkok').toFormat('dd/MM/yyyy');
    },
    onClickBroadcastAction(broadcast) {
      this.selectedBroadcastData = broadcast;
      this.visibleTooltipBroadcastID = this.visibleTooltipBroadcastID === this.selectedBroadcastData.id ? null : this.selectedBroadcastData.id;
    },
    onDeleteBroadcastAction() {
      this.isModalConfirmDeleteOpen = true;
    },
    onArchivedAction() {
      this.isModalConfirmArchiveOpen = true;
    },
    onCancelArchiveLineBroadcast() {
      this.isModalConfirmArchiveOpen = false;
      this.visibleTooltipBroadcastID = null;
    },
    async onConfirmArchiveLineBroadcast() {
      this.isLineBroadcastJobDataLoaded = false;
      const isArchiveBroadcastSuccess = await this.callArchiveBroadcastJobAPI(this.visibleTooltipBroadcastID);
      if (isArchiveBroadcastSuccess) {
        this.$buefy.toast.open({
          type: 'is-success',
          message: 'Broadcast has been successfully archived.',
          position: 'is-bottom',
        });
        this.clearLineBroadcastJobData();
        await this.getLineBroadcastJobList();
      } else {
        this.$buefy.toast.open({
          type: 'is-danger',
          message: 'Archived failed. Please try again.',
          position: 'is-bottom',
        });
      }
      this.isModalConfirmArchiveOpen = false;
      this.visibleTooltipBroadcastID = null;
      this.isLineBroadcastJobDataLoaded = true;
    },
    async callArchiveBroadcastJobAPI(id) {
      try {
        await this.archiveLineBroadcastJob(id);
        return true;
      } catch (error) {
        return false;
      }
    },
    async onCreateNewAction() {
      const canCreateNewLineBroadcast = await this.canCreateNewLineBroadcast();
      if (!canCreateNewLineBroadcast) return;
      this.$emit('on-media-storage-check', true);
      sessionStorage.removeItem('lineBroadcastCopiedData');
      this.$router.push({ path: '/line-broadcast/create' });
    },
    async canCreateNewLineBroadcast() {
      try {
        await this.checkAutomaticArchive();
        return true;
      } catch (error) {
        if (error.response && error.response.status === 429) {
          this.handleArchiveModalOpen(error.response.data.data);
        }
        return false;
      }
    },
    handleArchiveModalOpen(archiveData) {
      if (archiveData.canAutomaticallyArchive) {
        this.autoArchiveBroadcastId = archiveData.broadcastJobIdToBeAutomaticallyArchive;
        this.onOpenModalAutoArchive();
      } else {
        this.onOpenMediaStorageFullModal();
      }
    },
    onClickDownloadRecipientsCustomGroup: debounce(async (broadcastId) => {
      const response = await aliceApi.exportRecipientsCustomGroup(broadcastId);
      const fileName = getFilenameFromContentDispositionHelper(response);
      downloadFileHelper(response.data, fileName);
    }, 500),
    async onCopyAction() {
      const canCreateNewLineBroadcast = await this.canCreateNewLineBroadcast();
      if (!canCreateNewLineBroadcast) return;
      this.copyBroadcastJob();
      this.$emit('on-media-storage-check', true);
      this.$router.push({ path: '/line-broadcast/create' });
    },
    copyBroadcastJob() {
      const copiedBroadcast = {
        broadcastName: this.selectedBroadcastData.name,
        target: this.selectedBroadcastData.target,
        estimateRecipients: this.selectedBroadcastData.totalRecipient,
        selectedMessageList: this.formatCopyMessageList(this.selectedBroadcastData.listOfMessages),
        recipientsText: this.formatCopyListOfRecipientsToText(this.selectedBroadcastData.listOfRecipients),
        customTargetGroupName: this.selectedBroadcastData.customTargetGroupName || '',
        listOfAuthenticatedGroups: Array.isArray(this.selectedBroadcastData.listOfAuthenticatedGroups) && this.selectedBroadcastData.listOfAuthenticatedGroups.length > 0 ? this.selectedBroadcastData.listOfAuthenticatedGroups : [],
        listOfAreaCodes: Array.isArray(this.selectedBroadcastData.listOfAreaCodes) && this.selectedBroadcastData.listOfAreaCodes.length > 0 ? this.selectedBroadcastData.listOfAreaCodes : [],
      };
      sessionStorage.setItem('lineBroadcastCopiedData', JSON.stringify(copiedBroadcast));
    },
    formatCopyListOfRecipientsToText(listOfRecipients) {
      if (!Array.isArray(listOfRecipients) || listOfRecipients.length <= 0) return '';
      return listOfRecipients
        .filter((recipient) => typeof recipient === 'string' && recipient.trim() !== '')
        .map((recipient) => recipient.trim())
        .join('\n');
    },
    formatCopyMessageList(listOfMessages) {
      if (!Array.isArray(listOfMessages)) return [];
      const messageList = [];
      for (let i = 0; i < listOfMessages.length; i += 1) {
        const message = listOfMessages[i];
        const type = message.type.toUpperCase();
        const id = uniqid('broadcast_message-');
        if (type === 'TEXT') {
          messageList.push({
            id,
            textMessage: message.text,
            type,
          });
        } else if (type === 'IMAGE') {
          messageList.push({
            id,
            imageMessage: {
              filePath: message.originalContentUrl,
            },
            type,
          });
        } else if (type === 'VIDEO') {
          messageList.push({
            id,
            videoMessage: {
              filePath: message.originalContentUrl,
              previewImageUrl: message.previewImageUrl,
            },
            type,
          });
        } else if (type === 'IMAGEMAP') {
          if (message.video) {
            messageList.push({
              id,
              richVideoMessage: message,
              type: 'RICH_VIDEO',
            });
          } else {
            messageList.push({
              id,
              richImageMessage: message,
              type: 'RICH_IMAGE',
            });
          }
        }
      }
      return messageList;
    },
    onCancelDeleteLineBroadcast() {
      this.isModalConfirmDeleteOpen = false;
      this.visibleTooltipBroadcastID = null;
    },
    async onConfirmDeleteLineBroadcast() {
      this.isLineBroadcastJobDataLoaded = false;
      const isDeleteBroadcastSuccess = await this.callDeleteBroadcastJobAPI(this.visibleTooltipBroadcastID);
      if (isDeleteBroadcastSuccess) {
        this.$buefy.toast.open({
          type: 'is-success',
          message: 'Broadcast has been successfully deleted.',
          position: 'is-bottom',
        });
        this.clearLineBroadcastJobData();
        await this.getLineBroadcastJobList();
      } else {
        this.$buefy.toast.open({
          type: 'is-danger',
          message: 'Delete failed. Please try again.',
          position: 'is-bottom',
        });
      }
      this.isModalConfirmDeleteOpen = false;
      this.visibleTooltipBroadcastID = null;
      this.isLineBroadcastJobDataLoaded = true;
    },
    async callDeleteBroadcastJobAPI(id) {
      try {
        await this.deleteLineBroadcastJob(id);
        return true;
      } catch (error) {
        console.error('Error deleting broadcast:', error);
        return false;
      }
    },
    async infiniteHandler($state) {
      this.isLineBroadcastJobDataLoaded = false;
      if (!this.lineBroadcastJobData.isLast) {
        await this.getLineBroadcastJobList();
        $state.loaded();
      } else {
        $state.complete();
      }
      this.isLineBroadcastJobDataLoaded = true;
    },
    async onChangeCriteria() {
      this.isLineBroadcastJobDataLoaded = false;
      const { infiniteLoadingRef } = this.$refs;
      if (infiniteLoadingRef) {
        infiniteLoadingRef.stateChanger.reset();
      } else {
        await this.getLineBroadcastJobList();
      }
      this.clearLineBroadcastJobData();
      this.isLineBroadcastJobDataLoaded = true;
    },
    formatToISOStartOfDay(date) {
      if (!date) return null;
      return DateTime.fromJSDate(date).startOf('day').toUTC().toISO();
    },
    formatToISOEndOfDay(date) {
      if (!date) return null;
      return DateTime.fromJSDate(date).endOf('day').toUTC().toISO();
    },
    dateRangesSelectorSetup() {
      const startOfToday = this.currentDate.startOf('day').setZone('Asia/Bangkok');
      const endOfToday = startOfToday.endOf('day');

      const startOfYesterday = startOfToday.minus({ days: 1 });
      const endOfYesterday = startOfYesterday.endOf('day');

      const startOfLast7Days = startOfToday.minus(
        { days: 6 }
      );

      const startOfLast30Days = startOfToday.minus(
        { days: 29 }
      );

      const startOfThisMonth = startOfToday.startOf('month');
      const endOfThisMonth = startOfThisMonth.endOf('month');

      const startOfLastMonth = startOfToday.minus({ months: 1 }).startOf('month');
      const endOfLastMonth = startOfLastMonth.endOf('month');

      this.dateRangesSelector = {
        'All Dates': [null, null],
        Today: [startOfToday.toJSDate(), endOfToday.toJSDate()],
        Yesterday: [startOfYesterday.toJSDate(), endOfYesterday.toJSDate()],
        'Last 7 days': [startOfLast7Days.toJSDate(), endOfToday.toJSDate()],
        'This Month': [startOfThisMonth.toJSDate(), endOfThisMonth.toJSDate()],
        'Last Month': [startOfLastMonth.toJSDate(), endOfLastMonth.toJSDate()],
        'Last 30 days': [startOfLast30Days.toJSDate(), endOfToday.toJSDate()],
      };
    },
    displayTargetAudienceType(type, groupCount) {
      const label = this.targetAudience[type] ? this.targetAudience[type].label : 'Unknown';
      if (type === 'AUTHENTICATED' || type === 'AREA') {
        return `${label} (${groupCount})`;
      }
      return label;
    },
    displayBroadcastStatus(status) {
      if (status === 'SENT') {
        return 'Sent';
      }
      if (status === 'FAILED') {
        return 'Failed';
      }
      if (status === 'SCHEDULED') {
        return 'Scheduled';
      }
      if (status === 'IN_PROGRESS') {
        return 'In Progress';
      }
      return 'Unknown';
    },
    canCopyBroadcast(status) {
      return status === 'SENT' || status === 'FAILED' || status === 'SCHEDULED';
    },
    onOpenModalAutoArchive() {
      this.isModalAutoArchiveOpen = true;
    },
    onCancelAutoArchiveLineBroadcast() {
      this.isModalAutoArchiveOpen = false;
    },
    async onConfirmAutoArchiveLineBroadcast() {
      const isAutoArchiveBroadcastSuccess = await this.callArchiveBroadcastJobAPI(this.autoArchiveBroadcastId);
      if (isAutoArchiveBroadcastSuccess) {
        if (this.autoArchiveBroadcastId === this.visibleTooltipBroadcastID) {
          // This condition handles the case when the broadcast being auto-archived is the same as the one being copied
          this.copyBroadcastJob();
        }
        this.$emit('on-media-storage-check', true);
        this.$router.push({ path: '/line-broadcast/create' });
      } else {
        this.$buefy.toast.open({
          type: 'is-danger',
          message: 'Archived failed. Please try again.',
          position: 'is-bottom',
        });
      }
      this.isModalAutoArchiveOpen = false;
      this.autoArchiveBroadcastId = null;
    },
    onOpenMediaStorageFullModal() {
      this.isModalMediaStorageFullOpen = true;
    },
    onCloseModalMediaStorageFull() {
      this.isModalMediaStorageFullOpen = false;
    },
    formatAuthenticatedGroup(authenticatedGroups) {
      if (!Array.isArray(authenticatedGroups) || authenticatedGroups.length <= 0) return [];

      const authenticatedGroupSet = new Set(authenticatedGroups);
      return this.authenticatedGroupConstants.reduce((result, group) => {
        const matchedSubGroups = group.subGroups.filter((subGroup) => authenticatedGroupSet.has(subGroup.code));
        if (matchedSubGroups.length > 0) {
          result.push({
            id: group.id,
            name: group.groupName,
            subGroups: matchedSubGroups
          });
        }
        return result;
      }, []);
    },
    formatAreaCodes(areaCodes) {
      if (!Array.isArray(areaCodes) || areaCodes.length <= 0) return [];
      const result = [];
      const mapOfAreaIdToAreaName = SMMstaticData.AREA_GROUPS.reduce((acc, area) => {
        acc[area.id] = area.areaName;
        return acc;
      }, {});
      areaCodes.forEach((code) => {
        const areaName = mapOfAreaIdToAreaName[code];
        if (areaName) {
          result.push(areaName);
        }
      });
      return result;
    },
  },
  computed: {
    ...mapGetters({
      lineBroadcastJobData: 'lineBroadcast/lineBroadcastJobData'
    }),
  },
};
</script>

<style lang="scss" scoped>
.broadcast-list-page {
    p{
        margin: 0px;
    }
    .sub-header{
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 34px 45px;
        p{
            font-weight: 500;
            font-size: 16px;
            line-height: 120%;
            letter-spacing: 0px;
        }
        .new-broadcast-btn{
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: 24px;
            background-color:#4848FF;
            border: 0px;
            gap: 4px;
            color: #fff;
            font-size: 14px;
            font-weight: 500;
            line-height: 24px;
            letter-spacing: 0.32px;
            white-space: nowrap;
            padding: 10px 16px ;
            height: 40px;
            i{
                font-size: 20px;
                color: #fff;
            }
            &:focus{
                outline: none;
            }
            &:hover{
                background-color: #4848FF;
                box-shadow: 0px 4px 10px 0px #4848FF2B;
            }
            &:active{
                background-color: #4848FF;
            }
        }
        .new-broadcast-btn:hover {
          cursor: pointer;
        }
    }
    .broadcast-list-container{
        background-color: #fff;
        box-shadow: 0px 4px 16px 0px #868BA61A;
        border-radius: 16px;
        margin: 0px 40px 24px 40px;
        padding: 20px;
        .broadcast-filter{
            display: flex;
            justify-content: start;
            .broadcast-status-filter{
                width: 200px;
                position: relative;
            }
            .broadcast-date-filter{
                width: 230px;
                height: 40px;
                padding-right: 16px;
                .date-picker{
                    width: 100%;
                }
                .vue-daterange-picker ::v-deep .reportrange-text {
                    height: 40px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }
            }
            .status-selected{
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 8px 12px;
                border: 1px solid #DADCE4;
                border-radius: 8px;
                min-width: 130px;
                height: 40px;
                cursor: pointer;
                p{
                    font-weight: 400;
                    font-size: 14px;
                    line-height: 150%;
                    letter-spacing: 0px;
                    color: #111B49;
                }
                i{
                    color: #868BA6;
                    font-size: 20px;
                }
            }
            .status-dropdown{
                background-color: #FFFFFF;
                border-radius: 8px;
                border: 1px solid #DADCE4;
                position: absolute;
                margin-top: 4px;
                padding: 8px 12px;
                min-width: 130px;
                width: 200px;
                z-index: 10;
                .status-item{
                padding: 8px 0px;
                cursor: pointer;
                }
                .status-item.selected-status{
                padding: 8px 0px;
                color: #2962DF;
                }
            }
            .date-selected{
                display: flex;
                border-radius: 8px;
                p{
                    font-weight: 400;
                    font-size: 14px;
                    line-height: 150%;
                    letter-spacing: 0px;
                    color: #111B49;
                }
                i{
                    color: #4848FF;
                    font-size: 20px;
                    padding-right: 8px;
                }
            }
        }
        .broadcast-list-grid{
            padding-top: 20px;
            height: 580px;
            position: relative;
            .grid-header {
                display: grid;
                grid-template-columns: 20% 25% 15% 15% 20% 5%;
                font-weight: 600;
                font-size: 13px;
                letter-spacing: 0.17px;
                text-transform: uppercase;
                line-height: 150%;
                color: #0D173C;
                background-color: #F5F6FA;
                border-bottom: 1px solid #E7E8EE;
                position: sticky;
                top: 0;
                z-index: 5;
                .grid-header-item {
                    padding: 16px 12px;
                    &.grid-header-recipients {
                        text-align: right;
                    }
                }
            }
            .grid-content {
                display: flex;
                flex-direction: column;
                overflow-y: auto;
                max-height: calc(580px - 80px);
            }
            .grid-row {
                display: grid;
                grid-template-columns: 20% 25% 15% 15% 20% 5%;
                .grid-cell {
                    font-weight: 400;
                    font-size: 14px;
                    line-height: 150%;
                    letter-spacing: 0px;
                    color: #4B5178;
                    padding: 16px 12px;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                }
            }
            .infinite-loading-section{
                padding: 24px;
                height: 40px;
                grid-column: 1 / -1;
            }
            .no-data-section, .no-result-section {
                grid-column: 1 / -1;
            }
            .broadcast-item{
                border-bottom: 1px solid #E7E8EE;
                .recipients-custom-group {
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-end;
                    gap: 6px;
                    padding: 16px 3px 12px 12px !important;
                     .download-recipients-icon {
                          color: #4848FF;
                          cursor: pointer;
                     }
                }
                .status-badge-container{
                  padding-left: 20px;
                }
                .display-time-container{
                  padding-left: 24px;
                }
                .status-badge{
                    border-radius: 64px;
                    padding: 3.5px 6.5px;
                    width: max-content;
                    p{
                        font-weight: 500;
                        font-size: 10.5px;
                        line-height: 100%;
                        letter-spacing: 0px;
                    }
                }
                .badge{
                    &--sent{
                        color: #50CD89;
                        background-color: #EDFBF3;
                    }
                    &--failed{
                        color: #EF6767;
                        background-color: #FEEFEF;
                    }
                    &--scheduled{
                        color: #4848FF;
                        background-color: #EBEAFF;
                    }
                    &--in_progress{
                        color: #FEA955;
                        background-color: #FFF2E4;
                    }
                    &--archived{
                        color: #646A8C;
                        background-color: #FAFAFC;
                    }
                }
                .archive-badge{
                    margin-left: 8px;
                    border-radius: 64px;
                    padding: 3px 6.5px;
                    width: max-content;
                    color: #FFFFFF;
                    background-color: #646A8C;
                    font-weight: 500;
                    font-size: 10.5px;
                    line-height: 20px;
                    letter-spacing: 0px;
                    text-align: center;
                    vertical-align: middle;
                }
                .group-name-tag{
                  margin-top: 8px;
                }
                .register-group-tag{
                  display: flex;
                  flex-wrap: wrap;
                  gap: 8px;
                }
            }
            .action-tooltip-container {
                position: relative;
                cursor: pointer;
                .action-tooltip {
                    position: absolute;
                    width: 200px;
                    top: 20px;
                    right: 0;
                    background-color: #fff;
                    box-shadow: 0px 4px 8px 0px #868BA633;
                    border-radius: 8px;
                    padding: 12px 0px;
                    z-index: 10;
                    .tooltip-item{
                        display: flex;
                        align-items: center;
                        padding: 8px 12px;
                        cursor: pointer;
                        &:hover {
                            background-color: #F5F6FA;
                        }
                    }
                    p {
                      font-weight: 400;
                      font-size: 14px;
                      line-height: 150%;
                      letter-spacing: 0px;
                      vertical-align: middle;
                    }
                    i{
                      font-size: 20px;
                      padding-right: 8px;
                    }
                }
            }
        }
    }
    .confirmation-action-modal{
    position: absolute;
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100%;
    top: 0px;
    left: 0;
    z-index: 1041;
    background-color: rgba(0, 0, 0, 0.5);
    .card-content{
      display: flex;
      flex-direction: column;
      align-items: center;
      background-color: #fff;
      width: 500px;
      height: fit-content;
      border-radius: 16px;
      margin-top: 30px;
      text-align: center;
      position: relative;
      padding: 24px;
      .modal-title{
        color: #0D173C;
        font-size: 20px;
        font-weight:500;
        line-height: 24px;
        margin-top: 16px;
        width: 400px;
      }
      .warning-archive-icon{
        padding-bottom: 14px;
      }
      .modal-description{
        color: #646A8C;
        font-size: 14px;
        font-weight: 400;
        line-height: 21px;
        width: 100%;
        margin-top: 16px;
      }
      .truncated-text {
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .modal-confirm-send-now-footer{
        display: flex;
        justify-content: center;
        margin-top: 24px;
        .modal-btn{
          border-radius: 24px;
          padding: 8px 16px;
          border: unset;
          margin: 4px;
          &.cancel-btn{
            color: #EF6767;
            background-color: #fff;
            border: 1px solid #EF6767;
            &:hover{
              cursor: pointer;
            }
          }
          &.confirm-btn{
            color: #fff;
            background-color: #EF6767;
            &:hover{
              cursor: pointer;
            }
            &:disabled {
              background-color: #DADCE4;
              color: #868BA6;
              cursor: not-allowed;
            }
          }
          &:focus{
            outline: 0px;
          }
        }
      }
      .modal-archive-footer{
        padding-top: 24px;
        .modal-btn{
          border-radius: 4px;
          padding: 8px 16px;
          border: unset;
          margin: 5px;
          &.cancel-btn{
            color: #F44336;
            background-color: #fff;
            border: 1px solid #F44336;
            &:hover{
              cursor: pointer;
            }
          }
          &.confirm-btn{
            color: #fff;
            background-color: #F44336;
            &:hover{
              cursor: pointer;
            }
            &:disabled {
              background-color: #DADCE4;
              color: #868BA6;
              cursor: not-allowed;
            }
          }
          &:focus{
            outline: 0px;
          }
        }
      }
      .modal-auto-archive-footer{
        padding-top: 24px;
        .modal-btn{
          border-radius: 24px;
          padding: 8px 16px;
          border: unset;
          margin: 5px;
          &.cancel-btn{
            color: #FEA955;
            background-color: #fff;
            border: 1px solid #DADCE4;
            &:hover{
              cursor: pointer;
            }
          }
          &.confirm-btn{
            color: #fff;
            background-color: #FEA955;
            &:hover{
              cursor: pointer;
            }
            &:disabled {
              background-color: #DADCE4;
              color: #868BA6;
              cursor: not-allowed;
            }
          }
          &:focus{
            outline: 0px;
          }
        }
      }
      .close-icon{
        position: absolute;
        top: 20px;
        right: 14px;
        width: 30px;
        height: 30px;
        display: flex;
        justify-content: center;
        cursor: pointer;
        i{
          color: #0000007F;
          font-size: 24px;
        }
      }
    }
  }
  .no-data-broadcast{
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding-top: 80px;
      .no-data-text{
          font-size: 16px;
          font-weight: 500;
          line-height: 120%;
          letter-spacing: 0px;
          text-align: center;
          color: #666D92;
          padding-top: 18px;
          padding-bottom: 8px;
      }
      img{
          width: 168px;
          height: 88px;
      }
      p{
          font-weight: 400;
          font-size: 14px;
          line-height: 150%;
          letter-spacing: 0px;
          color: #868BA6;
      }
      .link-text{
          color: #4848FF;
          cursor: pointer;
      }
  }
  .no-result-broadcast{
    padding-top: 80px;
  }
}
</style>
